import React, { useEffect, useState } from "react";
import { SERVER_URL } from "../../config/config";
import Loader from "../Loader";
import "./RevenueGrid.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function RevenuesGrid({ revenueType, searchTerm }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const getToday = () => new Date().toISOString().split("T")[0];

  const [dateFromInput, setDateFromInput] = useState(new Date("2026-01-01"));
  const [dateToInput, setDateToInput] = useState(new Date());

  const [dateFrom, setDateFrom] = useState("2026-01-01");
  const [dateTo, setDateTo] = useState(getToday());

  useEffect(() => {
    if (!revenueType) {
      setRows([]);
      return;
    }

    if (revenueType === "transport") {
      fetchTransportRevenue();
    }

    if (revenueType === "saral") {
      fetchSaralRevenue();
    }
  }, [revenueType, dateFrom, dateTo]);

  const fetchTransportRevenue = async () => {
    try {
      setLoading(true);

      const res = await fetch(
        SERVER_URL + "/tie_details/transport_ewaybill_revenue",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            date_dict: {
              date_from: dateFrom,
              date_to: dateTo,
            },
            filter_fields: {
              company_name: "",
              client_id: "",
            },
            sort_fields: [],
          }),
        },
      );

      const data = await res.json();
      setRows(data && Array.isArray(data.company) ? data.company : []);
    } catch (err) {
      console.error("Transport revenue fetch failed", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSaralRevenue = async () => {
    try {
      setLoading(true);

      const res = await fetch(SERVER_URL + "/tie_details/saral_eway_revenue", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          date_dict: {
            date_from: dateFrom,
            date_to: dateTo,
          },
          filter_fields: {
            company_name: "",
            client_id: "",
          },
          sort_fields: [],
        }),
      });

      const data = await res.json();
      setRows(data && Array.isArray(data.company) ? data.company : []);
    } catch (err) {
      console.error("Saral revenue fetch failed", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const safeLower = (val) => (typeof val === "string" ? val.toLowerCase() : "");

  const calculateProfit = (row) => {
    const charge =
      revenueType === "transport"
        ? Number(row.eway_bill_charge || 0)
        : Number(row.last_payment || 0);

    const apiCost = Number(row.api_cost || 0);

    const profit = charge - apiCost;
    const percentage =
      charge > 0 ? ((profit / charge) * 100).toFixed(1) : "0.0";

    return { profit, percentage, charge };
  };

  const filteredRows = rows.filter((row) => {
    if (!searchTerm) return true;

    const search = safeLower(searchTerm);

    return (
      safeLower(row.company_name).includes(search) ||
      safeLower(row.gst_number || row.gst_no).includes(search) ||
      String(row.client_id || row.id).includes(search)
    );
  });

  if (!revenueType) {
    return <div className="revenue-empty">Please select a revenue type</div>;
  }

  if (loading) {
    return <Loader text="Loading revenue data..." />;
  }

  return (
    <div className="revenue-table-wrap">
      <div className="revenue-header revenue-header-row">
        <h2>
          {revenueType === "transport"
            ? "Transport Revenue"
            : "Saral E-Way Revenue"}
        </h2>

        <div className="revenue-date-filters">
          <label>
            From
            <DatePicker
              selected={dateFromInput}
              onChange={(date) => {
                setDateFromInput(date);
                setDateFrom(date.toISOString().split("T")[0]);
              }}
              dateFormat="dd-MM-yyyy"
            />
          </label>

          <label>
            To
            <DatePicker
              selected={dateToInput}
              onChange={(date) => {
                setDateToInput(date);
                setDateTo(date.toISOString().split("T")[0]);
              }}
              dateFormat="dd-MM-yyyy"
            />
          </label>
        </div>
      </div>

      <table className="revenue-table">
        <thead>
          <tr>
            <th>Sr No.</th>
            <th>Company</th>
            {revenueType === "saral" && <th>Company ID</th>}
            <th>GST</th>
            <th>API Count</th>
            <th>Eway Bill Charge (₹)</th>
            {revenueType === "saral" && <th>Plan</th>}
            <th>API Cost (₹)</th>
            <th>Profit</th>
          </tr>
        </thead>

        <tbody>
          {filteredRows.length === 0 ? (
            <tr>
              <td
                colSpan={revenueType === "saral" ? 9 : 7}
                className="revenue-no-data"
              >
                No matching data found
              </td>
            </tr>
          ) : (
            filteredRows.map((row, idx) => {
              const { profit, percentage, charge } = calculateProfit(row);

              return (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>{row.company_name}</td>

                  {revenueType === "saral" && <td>{row.id}</td>}

                  <td>{row.gst_number || row.gst_no}</td>
                  <td>{row.total_count}</td>

                  <td className="amount">₹{charge.toLocaleString("en-IN")}</td>

                  {revenueType === "saral" && (
                    <td>
                      {row.plan
                        ? row.plan.charAt(0).toUpperCase() + row.plan.slice(1)
                        : "-"}
                    </td>
                  )}

                  <td>₹{Number(row.api_cost).toLocaleString("en-IN")}</td>

                  <td className="profit">
                    ₹{profit.toLocaleString("en-IN")} ({percentage}%)
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
